/**
 * Wistia Video Progress Tracker
 *
 * Tracks video progress for the Wistia Player Web Component (`<wistia-player>`)
 * and POSTs progress updates to the server. Also handles resume-from-last-position.
 *
 * @see {@link https://docs.wistia.com/docs/wistia-player-web-component} Wistia Player Web Component
 */

function initVideoProgressTracker() {
  const playerEl = document.querySelector('wistia-player[data-video-id]');

  if (!playerEl) {
    return;
  }

  const videoId = playerEl.getAttribute('data-video-id');

  if (!videoId) {
    return;
  }

  let lastSentAt = 0;
  let lastProgress = -1;

  const resumeProgress = Number(playerEl.dataset.resumeProgress || 0);
  const resumeCompleted = playerEl.dataset.resumeCompleted === '1';

  // Note: this endpoint is intentionally named "save" rather than "track" —
  // ad-blocker filter lists (uBlock Origin, AdGuard, EasyPrivacy, Brave
  // Shields, etc.) commonly block any request whose path contains the word
  // "track" by default, even for first-party, same-origin requests. That
  // silently dropped legitimate progress updates for a meaningful slice of
  // real, logged-in visitors.
  const PROGRESS_URL = '/video-progress/save';

  const sendProgress = (progress, completed = false, force = false) => {
    const clamped = Math.max(0, Math.min(100, Number(progress) || 0));
    const now = Date.now();

    if (!force) {
      const withinThrottleWindow = now - lastSentAt < 5000;
      const smallProgressDelta = Math.abs(clamped - lastProgress) < 2;

      if (withinThrottleWindow && smallProgressDelta) {
        return;
      }
    }

    lastSentAt = now;
    lastProgress = clamped;

    const body = {
      videoId,
      progress: clamped,
      completed,
    };

    if (window.csrfTokenName && window.csrfToken) {
      body[window.csrfTokenName] = window.csrfToken;
    }

    const payload = JSON.stringify(body);

    // `force` beats fire on pause/end/visibilitychange/beforeunload — i.e.
    // exactly when the page may be torn down. A plain fetch() can be
    // aborted by the browser mid-flight during unload, silently losing the
    // most important beat. navigator.sendBeacon() is purpose-built to
    // guarantee delivery in that situation, so prefer it whenever possible.
    if (force && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      const sent = navigator.sendBeacon(PROGRESS_URL, blob);

      if (sent) {
        return;
      }
      // Fall through to fetch() if the beacon couldn't be queued.
    }

    fetch(PROGRESS_URL, {
      method: 'POST',
      credentials: 'same-origin',
      // keepalive lets the request survive page unload/navigation, similar
      // to sendBeacon, for browsers/paths that don't use the beacon above.
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': window.csrfToken || '',
      },
      body: payload,
    })
      .then((response) => {
        if (!response.ok) {
          console.warn(
            `Video progress save request failed (${response.status}).`,
          );
        }
      })
      .catch((error) => {
        console.debug('Video progress tracking failed:', error);
      });
  };

  const getPercent = () => {
    const elapsed = playerEl.currentTime;
    const duration = playerEl.duration;

    if (
      !Number.isFinite(elapsed) ||
      !Number.isFinite(duration) ||
      duration <= 0
    ) {
      return null;
    }

    return (elapsed / duration) * 100;
  };

  const onReady = () => {
    // Resume from last saved position
    if (
      !resumeCompleted &&
      Number.isFinite(resumeProgress) &&
      resumeProgress >= 2 &&
      resumeProgress < 95
    ) {
      const duration = playerEl.duration;

      if (Number.isFinite(duration) && duration > 0) {
        const targetSeconds = Math.max(
          0,
          Math.min(duration - 2, (duration * resumeProgress) / 100),
        );

        if (targetSeconds >= 3) {
          playerEl.currentTime = targetSeconds;
          lastProgress = Math.max(lastProgress, resumeProgress);
        }
      }
    }

    playerEl.addEventListener('timeupdate', () => {
      const percent = getPercent();
      if (percent !== null) {
        sendProgress(percent, percent >= 95, false);
      }
    });

    playerEl.addEventListener('pause', () => {
      const percent = getPercent();
      if (percent !== null) {
        sendProgress(percent, percent >= 95, true);
      }
    });

    playerEl.addEventListener('end', () => {
      sendProgress(100, true, true);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        const percent = getPercent();
        if (percent !== null) {
          sendProgress(percent, percent >= 95, true);
        }
      }
    });

    window.addEventListener('beforeunload', () => {
      const percent = getPercent();
      if (percent !== null) {
        sendProgress(percent, percent >= 95, true);
      }
    });
  };

  // Wait for the <wistia-player> custom element to be registered, then initialise
  customElements.whenDefined('wistia-player').then(() => {
    if (playerEl.duration > 0) {
      // Player already has metadata — initialise immediately
      onReady();
    } else {
      // Wait for the player to signal it's ready with a valid duration
      playerEl.addEventListener('ready', onReady, { once: true });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoProgressTracker);
} else {
  initVideoProgressTracker();
}
